import React, { useState, useEffect } from 'react';
import { Users, ChevronLeft, ChevronRight } from 'lucide-react';
import { Tour } from '../../types/tours';
import { BookingGroupType } from '../../types/bookings';

interface BookingGroupStepProps {
  tour: Tour;
  groupType: BookingGroupType | null;
  persons: number;
  onChange: (data: { groupType: BookingGroupType; persons: number; totalPrice: number }) => void;
  onBack: () => void;
  onNext: () => void;
}

const BookingGroupStep: React.FC<BookingGroupStepProps> = ({
  tour,
  groupType,
  persons,
  onChange,
  onBack,
  onNext
}) => {
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Set initial persons count to minimum required
    if (persons < tour.min_persons) {
      const newPersons = tour.min_persons;
      if (groupType) {
        const price = calculatePrice(groupType, newPersons);
        onChange({ groupType, persons: newPersons, totalPrice: price });
      }
    }

    // Auto-select tour option if none selected
    if (!groupType) {
      // Get available tour types and their prices
      const options = [
        { type: 'group', price: tour.group_tour_price },
        { type: 'reduced', price: tour.reduced_group_price || 0 },
        { type: 'private', price: tour.private_price || 0 }
      ].filter(opt => opt.price > 0) as { type: BookingGroupType; price: number }[];

      if (options.length === 1) {
        // If only one option available, select it
        const price = calculatePrice(options[0].type, persons);
        onChange({ groupType: options[0].type, persons, totalPrice: price });
      } else if (options.length > 1) {
        // Select the most expensive option
        const mostExpensive = options.reduce((prev, curr) => 
          curr.price > prev.price ? curr : prev
        );
        const price = calculatePrice(mostExpensive.type, persons);
        onChange({ groupType: mostExpensive.type, persons, totalPrice: price });
      }
    }
  }, [tour, groupType, persons]);

  const handleGroupTypeSelect = (type: BookingGroupType) => {
    const price = calculatePrice(type, persons);
    onChange({ groupType: type, persons, totalPrice: price });
    if (type === 'group' && persons > 10) {
      setError('Group tours are limited to 10 persons maximum');
      return;
    }
    if (type === 'reduced' && persons > 6) {
      setError('Reduced groups are limited to 6 persons maximum');
      return;
    }
    setError(null);
  };

  const handlePersonsChange = (newPersons: number) => {
    if (newPersons < tour.min_persons) {
      setError(`This tour requires a minimum of ${tour.min_persons} ${tour.min_persons === 1 ? 'person' : 'persons'}`);
      return;
    }

    if (groupType === 'group' && newPersons > 10) {
      setError('Group tours are limited to 10 persons maximum');
      return;
    }
    if (groupType === 'reduced' && newPersons > 6) {
      setError('Reduced groups are limited to 6 persons maximum');
      return;
    }
    if (groupType) {
      const price = calculatePrice(groupType, newPersons);
      onChange({ groupType, persons: newPersons, totalPrice: price });
    }
    setError(null);
  };

  const calculatePrice = (type: BookingGroupType, count: number): number => {
    switch (type) {
      case 'group':
        return tour.group_tour_price * count;
      case 'reduced':
        return (tour.reduced_group_price || tour.group_tour_price) * count;
      case 'private':
        return tour.private_price * count;
      default:
        return 0;
    }
  };

  const handleNext = () => {
    if (!groupType) {
      setError('Please select a group type');
      return;
    }
    if (persons < 1) {
      setError('Please select at least 1 person');
      return;
    }
    if (persons < tour.min_persons) {
      setError(`This tour requires a minimum of ${tour.min_persons} ${tour.min_persons === 1 ? 'person' : 'persons'}`);
      return;
    }
    if (groupType === 'group' && persons > 10) {
      setError('Group tours are limited to 10 persons maximum');
      return;
    }
    if (groupType === 'reduced' && persons > 6) {
      setError('Reduced groups are limited to 6 persons maximum');
      return;
    }
    onNext();
  };

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold text-[#2A382A] mb-2">Select Group Size</h2>
        <p className="text-[#2A382A]/70">
          Choose your group type and the number of persons. Group tours are limited to 10 persons.
        </p>
      </div>

      {/* Group Type Selection */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Group Tour - Always show as it's mandatory */}
        {tour.group_tour_price > 0 && (
          <button
            onClick={() => handleGroupTypeSelect('group')}
            aria-label="Select group tour option"
            className={`p-6 rounded-xl border-2 transition-all ${
              groupType === 'group'
                ? 'border-[#953B0E] bg-[#953B0E]/5'
                : 'border-[#2A382A]/10 hover:border-[#953B0E]/50'
            }`}
          >
            <div className="text-left">
              <h3 className="text-lg font-semibold text-[#2A382A] mb-1">Group Tour</h3>
              <p className="text-sm text-[#2A382A]/70 mb-4">Join other adventurers</p>
              <div className="text-xl font-bold text-[#2A382A]">
                ${tour.group_tour_price}
                <span className="text-sm font-normal text-[#2A382A]/70 ml-1">per person</span>
              </div>
            </div>
          </button>
        )}

        {/* Reduced Group - Only show if price exists and is greater than 0 */}
        {tour.reduced_group_price && tour.reduced_group_price > 0 && (
          <button
            onClick={() => handleGroupTypeSelect('reduced')}
            aria-label="Select reduced group tour option"
            className={`p-6 rounded-xl border-2 transition-all ${
              groupType === 'reduced'
                ? 'border-[#953B0E] bg-[#953B0E]/5'
                : 'border-[#2A382A]/10 hover:border-[#953B0E]/50'
            }`}
          >
            <div className="text-left">
              <h3 className="text-lg font-semibold text-[#2A382A] mb-1">Reduced Group</h3>
              <p className="text-sm text-[#2A382A]/70 mb-4">Special rate for groups</p>
              <div className="text-xl font-bold text-[#2A382A]">
                ${tour.reduced_group_price}
                <span className="text-sm font-normal text-[#2A382A]/70 ml-1">per person</span>
              </div>
            </div>
          </button>
        )}

        {/* Private Tour - Only show if price exists and is greater than 0 */}
        {tour.private_price && tour.private_price > 0 && (
          <button
            onClick={() => handleGroupTypeSelect('private')}
            aria-label="Select private tour option"
            className={`p-6 rounded-xl border-2 transition-all ${
              groupType === 'private'
                ? 'border-[#953B0E] bg-[#953B0E]/5'
                : 'border-[#2A382A]/10 hover:border-[#953B0E]/50'
            }`}
          >
            <div className="text-left">
              <h3 className="text-lg font-semibold text-[#2A382A] mb-1">Private Tour</h3>
              <p className="text-sm text-[#2A382A]/70 mb-4">Exclusive experience</p>
              <div className="text-xl font-bold text-[#2A382A]">
                ${tour.private_price}
                <span className="text-sm font-normal text-[#2A382A]/70 ml-1">per person</span>
              </div>
            </div>
          </button>
        )}
      </div>

      {/* Number of Persons */}
      <div className="bg-[#F2F3F4] rounded-xl p-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white rounded-lg">
              <Users className="w-5 h-5 text-[#2A382A]" />
            </div>
            <div className="flex-1">
              <h3 className="font-medium text-[#2A382A]">Number of Persons</h3>
              <p className={`text-sm ${error ? 'text-red-500' : 'text-[#2A382A]/70'}`}>
                Minimum {tour.min_persons} {tour.min_persons === 1 ? 'person' : 'persons'} required •{' '}
                {groupType === 'group' ? 'Maximum 10 persons' : 'No maximum limit'}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-0">
            <button
              onClick={() => handlePersonsChange(Math.max(1, persons - 1))}
              aria-label="Decrease number of persons"
              className={`w-8 h-8 flex items-center justify-center bg-white rounded-lg transition-colors ${persons <= tour.min_persons ? 'opacity-50 cursor-not-allowed' : 'hover:bg-[#2A382A]/5'}`}
              disabled={persons <= tour.min_persons}
            >
              <ChevronLeft className="w-5 h-5 text-[#2A382A]" />
            </button>
            <span className="w-12 text-center font-medium text-[#2A382A]">{persons}</span>
            <button
              onClick={() => handlePersonsChange(persons + 1)}
              aria-label="Increase number of persons"
              className="w-8 h-8 flex items-center justify-center bg-white rounded-lg hover:bg-[#2A382A]/5 transition-colors"
              disabled={(groupType === 'group' && persons >= 10) || (groupType === 'reduced' && persons >= 6)}
            >
              <ChevronRight className="w-5 h-5 text-[#2A382A]" />
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-500/10 text-red-500 p-4 rounded-lg">
          {error}
        </div>
      )}

      {/* Navigation */}
      <div className="flex justify-between">
        <button
          onClick={onBack}
          aria-label="Go back to previous step"
          className="px-6 py-2 bg-white border border-[#2A382A]/10 text-[#2A382A] rounded-lg hover:bg-[#2A382A]/5 transition-colors"
        >
          Back
        </button>
        <button
          onClick={handleNext}
          aria-label="Continue to next step"
          className="px-6 py-2 bg-[#953B0E] text-white rounded-lg hover:bg-[#ff6a0d] transition-colors border-2 border-[#E85C0D]"
        >
          Continue
        </button>
      </div>
    </div>
  );
};

export default BookingGroupStep;